import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import productsHeroImage from '../assets/ourproducts/pexels-martabranco-1395306 (1).jpg';
import goldJewelleryImage from '../assets/Images/84.jpg';
import silverJewelleryImage from '../assets/Images/3.jpeg';
import preciousStonesImage from '../assets/Images/18.jpeg';
import semiPreciousStonesImage from '../assets/Images/25.jpeg';
import logo1 from '../assets/Images/20.jpeg';
import logo2 from '../assets/Images/28.jpeg';
import logo3 from '../assets/Images/32.jpeg';
import logo4 from '../assets/Images/40.jpeg';
import logo5 from '../assets/Images/65.jpeg';
import logo6 from '../assets/Images/68.jpeg';
import logo7 from '../assets/Images/36.jpeg';
import logo8 from '../assets/Images/26.jpeg';
import logo9 from '../assets/Images/12.jpeg';
import logo10 from '../assets/Images/2.jpeg';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Animation variants
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const OurProducts = () => {
    const { t } = useTranslation('ourproducts');

    const jewelleriesAndStones = [
        {
            img: goldJewelleryImage,
            title: t('categories.gold.title'),
            desc: t('categories.gold.desc'),
            link: "/gold-jewellery",
        },
        {
            img: silverJewelleryImage,
            title: t('categories.silver.title'),
            desc: t('categories.silver.desc'),
            link: "/silver-jewellery",
        },
        {
            img: preciousStonesImage,
            title: t('categories.precious.title'),
            desc: t('categories.precious.desc'),
            link: "/precious-stones",
        },
        {
            img: semiPreciousStonesImage,
            title: t('categories.semiPrecious.title'),
            desc: t('categories.semiPrecious.desc'),
            link: "/semi-precious-stones",
        },
    ];

    const logos = [
        logo1, logo2, logo3, logo4, logo5,
        logo6, logo7, logo8, logo9, logo10,
    ];

    const mobileSettings = { dots: true, infinite: true, speed: 800, slidesToShow: 1, slidesToScroll: 1, arrows: false };
    const tabSettings = { dots: true, infinite: true, speed: 800, slidesToShow: 2, slidesToScroll: 1, arrows: false };
    const logoSliderSettings = {
        dots: false, infinite: true, speed: 5000, slidesToShow: 4, slidesToScroll: 1,
        autoplay: true, autoplaySpeed: 0, cssEase: "linear", arrows: false, pauseOnHover: false,
        variableWidth: true,
        responsive: [{ breakpoint: 1024, settings: { slidesToShow: 4 } }, { breakpoint: 768, settings: { slidesToShow: 3 } }],
    };
    const smLogoSliderSettings = {
        dots: false, infinite: true, speed: 3000, slidesToShow: 2, slidesToScroll: 1,
        autoplay: true, autoplaySpeed: 0, cssEase: "linear", arrows: false, pauseOnHover: false,
        variableWidth: true,
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Hero Section */}
            <div className="relative flex h-[80vh] w-full items-center justify-center font-lato md:h-[60vh] lg:h-[80vh]">
                <img
                    src={productsHeroImage}
                    alt={t('hero.alt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="container relative z-10 flex items-center justify-center pt-[100px]"
                >
                    <div className="w-full">
                        <div className="bg-gradient-to-t from-[#1010106c] to-[#ffffff] bg-clip-text text-center text-[25px] uppercase leading-[50px] text-transparent lg:text-[40px] lg:leading-[74px]">
                            {t('hero.timeless')}
                        </div>
                        <h1 className="text-center text-[50px] font-semibold leading-[60px] text-white lg:text-[80px] lg:leading-[80px] font-copperplate">
                            {t('hero.title')}
                        </h1>
                        <div className="mb-[-20px] mt-[40px] flex justify-end text-white">
                            <div className="mr-[20px] w-[50%] text-right text-[16px] lg:mr-0 lg:w-[25%]">
                                {t('hero.description')}
                            </div>
                        </div>

                        <div className="absolute left-[30px] top-10">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px]">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" />
                            </svg>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Jewelleries and Stones Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container relative font-lato text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('section.title')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('section.subtitle')}
                        </div>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    {/* Desktop Grid */}
                    <div className="mt-5 hidden grid-cols-1 gap-8 lg:grid lg:grid-cols-4">
                        {jewelleriesAndStones.map((item, index) => (
                            <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp}
                                className="group relative h-[419px] w-full overflow-hidden rounded-tl-3xl shadow-lg">
                                <img src={item.img} alt={item.title} className="object-cover transition-transform duration-500 group-hover:scale-110 h-full w-full" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                                <div className="absolute bottom-0 left-0 w-full p-4">
                                    <h2 className="text-[24px] font-medium text-[#BF943A] transition-all duration-500 group-hover:translate-y-[-10px]">
                                        {item.title}
                                    </h2>
                                    <div className="mt-2 max-h-0 overflow-hidden opacity-0 transition-all duration-500 group-hover:max-h-[200px] group-hover:opacity-100">
                                        <p className="text-[16px] text-white">{item.desc}</p>
                                        <div className="flex justify-end">
                                            <a href={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-white">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </a>
                                        </div>
                                        <div className="flex justify-end mt-2">
                                            <a href={item.link} className="text-[16px] font-semibold text-[#BF943A] hover:underline">
                                                {t('viewMore')}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile & Tablet Sliders */}
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="block md:hidden">
                        <Slider {...mobileSettings}>
                            {jewelleriesAndStones.map((item, index) => (
                                <div key={index} className="px-4">
                                    <div className="group relative h-[419px] w-full overflow-hidden rounded-tl-3xl shadow-lg">
                                        <img src={item.img} alt={item.title} className="object-cover transition-transform duration-500 group-hover:scale-110 h-full w-full" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                                        <div className="absolute bottom-0 left-0 w-full p-4">
                                            <h2 className="text-[24px] font-medium text-[#BF943A] transition-all duration-500 group-hover:translate-y-[-10px]">
                                                {item.title}
                                            </h2>
                                            <div className="mt-2 max-h-0 overflow-hidden opacity-0 transition-all duration-500 group-hover:max-h-[200px] group-hover:opacity-100">
                                                <p className="text-[16px] text-white">{item.desc}</p>
                                                <div className="flex justify-end mt-2">
                                                    <a href={item.link} className="text-[16px] font-semibold text-[#BF943A] hover:underline">
                                                        {t('viewMore')}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="hidden md:block lg:hidden">
                        <Slider {...tabSettings}>
                            {jewelleriesAndStones.map((item, index) => (
                                <div key={index} className="px-4">
                                    <div className="group relative h-[419px] w-full overflow-hidden rounded-tl-3xl shadow-lg">
                                        <img src={item.img} alt={item.title} className="object-cover transition-transform duration-500 group-hover:scale-110 h-full w-full" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                                        <div className="absolute bottom-0 left-0 w-full p-4">
                                            <h2 className="text-[24px] font-medium text-[#BF943A] transition-all duration-500 group-hover:translate-y-[-10px]">
                                                {item.title}
                                            </h2>
                                            <div className="mt-2 max-h-0 overflow-hidden opacity-0 transition-all duration-500 group-hover:max-h-[200px] group-hover:opacity-100">
                                                <p className="text-[16px] text-white">{item.desc}</p>
                                                <div className="flex justify-end mt-2">
                                                    <a href={item.link} className="text-[16px] font-semibold text-[#BF943A] hover:underline">
                                                        {t('viewMore')}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>
                </div>
            </section>

            {/* Logo Sliders remain unchanged (logos are images) */}
            <section className="block sm:hidden w-full py-6 bg-[#1F1F1F] overflow-hidden">
                <div className="max-w-md mx-auto px-2">
                    <Slider {...smLogoSliderSettings}>
                        {logos.map((logo, index) => (
                            <div key={index} className="flex justify-center px-2">
                                <div className="w-[120px] h-[120px] flex items-center justify-center rounded-lg bg-white/5 p-2">
                                    <img src={logo} alt={`Logo ${index + 1}`} className="max-w-full max-h-full object-contain" loading="lazy" />
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>

            <section className="hidden sm:block w-full py-10 bg-[#1F1F1F] overflow-hidden">
                <div className="max-w-7xl mx-auto px-4">
                    <Slider {...logoSliderSettings}>
                        {logos.map((logo, index) => (
                            <div key={index} className="flex justify-center px-4">
                                <div className="relative w-[200px] h-[200px] flex items-center justify-center rounded-lg">
                                    <img src={logo} alt={`Logo ${index + 1}`} className="object-contain p-2" loading="lazy" />
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>
        </div>
    );
};

export default OurProducts;